import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { quotaApi } from "@/lib/api";

/**
 * Surfaces the lowest remaining AI bucket across the four rate-limited
 * endpoints. Polls /api/ai/quota every 60s. Goes amber at ≤5 remaining,
 * red at 0. Tooltip shows the per-endpoint breakdown.
 */
export default function AiQuotaChip() {
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const q = await quotaApi.get();
        if (!cancelled) setQuota(q);
      } catch (e) {
        // Telemetry-only — surface as debug so DevTools still shows it but it
        // never pops a user-facing toast.
        console.debug("ai-quota poll failed", e);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!quota) return null;

  const entries = [
    ["command", "cmd", 30],
    ["estimate", "est", 60],
    ["subtasks", "sub", 30],
    ["daily_wrap", "wrap", 6],
  ];
  // Lowest ratio (remaining / capacity) is the most pressing.
  const ratios = entries.map(([k, , cap]) => ({ k, remaining: quota[k] ?? cap, cap }));
  const min = ratios.reduce((a, b) => (a.remaining / a.cap < b.remaining / b.cap ? a : b));

  const tone =
    min.remaining === 0 ? "text-red border-red/50 bg-red/10"
    : min.remaining <= 5 ? "text-amber border-amber/40 bg-amber/5"
    : "text-ink-dim border-line";

  const tooltip = entries
    .map(([k, label, cap]) => `${label} ${quota[k] ?? cap}/${cap}`)
    .join(" · ");

  return (
    <div
      className={clsx(
        "mono text-2xs uppercase tracking-widest px-1.5 py-0.5 border tabular-nums",
        tone
      )}
      title={`AI budget — ${tooltip}`}
      data-testid="ai-quota-chip"
    >
      <span className="hidden xl:inline">AI · </span>
      {min.remaining}<span className="opacity-60">/{min.cap}</span>
    </div>
  );
}
